import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { actionPlanKeys, fetchActionPlanSearchV2 } from '@/core/api/queries';

/**
 * search-v2 bucket IDs for ACTION_PLAN_STATUS_CHANGED → homepage CTA deep links.
 *
 * @param {{ enabled?: boolean }} [options]
 * @returns {{
 *   draftActionPlanId: string|null,
 *   implementationActionPlanId: string|null,
 *   completedActionPlanIds: string[],
 *   isLoading: boolean,
 * }}
 */
export function useNotificationActionPlanCtaIds({ enabled = false } = {}) {
  const queryClient = useQueryClient();
  const [draftActionPlanId, setDraftActionPlanId] = useState(null);
  const [implementationActionPlanId, setImplementationActionPlanId] = useState(null);
  const [completedActionPlanIds, setCompletedActionPlanIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setDraftActionPlanId(null);
      setImplementationActionPlanId(null);
      setCompletedActionPlanIds([]);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const apData = await queryClient.fetchQuery({
          queryKey: actionPlanKeys.searchV2(),
          queryFn: fetchActionPlanSearchV2,
        });
        if (cancelled) return;
        const draftId = apData?.draft?.id;
        const implementationId = apData?.implementation?.id;
        const completed = Array.isArray(apData?.completed) ? apData.completed : [];
        setDraftActionPlanId(draftId != null && draftId !== '' ? String(draftId) : null);
        setImplementationActionPlanId(
          implementationId != null && implementationId !== '' ? String(implementationId) : null,
        );
        setCompletedActionPlanIds(
          completed
            .map((ap) => ap?.id)
            .filter((id) => id != null && id !== '')
            .map(String),
        );
      } catch {
        if (!cancelled) {
          setDraftActionPlanId(null);
          setImplementationActionPlanId(null);
          setCompletedActionPlanIds([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, queryClient]);

  return useMemo(
    () => ({
      draftActionPlanId,
      implementationActionPlanId,
      completedActionPlanIds,
      isLoading,
    }),
    [draftActionPlanId, implementationActionPlanId, completedActionPlanIds, isLoading],
  );
}
